import {
  DishAllergen,
  DishIngredient,
  DishPreference,
  DishResponse,
  DishTag,
  GetAllResolvedEdiblesResponse,
  NutritionEU,
} from "api-types";

import React, { useLayoutEffect, useMemo, useState } from "react";

import type { Theme } from "@mui/material";
import {
  Box,
  IconButton,
  Stack,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { SegmentedControl } from "../../../admin/SegmentedControl/SegmentedControl";
import { useDishDetailLoader } from "../../../contexts/DishDetailLoader";
import { useTranslation } from "../../../i18n";
import {
  AllergenChipOutlined,
  PreferenceChipOutlined,
  TagChip,
} from "../../Chip/Chip";
import { DynamicDrawer } from "../../Drawer/DynamicDrawer";
import CloseIcon from "../../Icons/Close";
import KnifeAndForkIcon from "../../Icons/KnifeAndFork";
import { ImagePreview } from "../../ImagePreview/ImagePreview";
import { CircularProgressCenter } from "../../Loading/CircularProgressCenter";
import {
  SpiceLevelIcon,
  isSpiceLevel,
} from "../../SpiceLevelIcon/SpiceLevelIcon";
import { DishInfoMayContain } from "./DishInfoMayContain";
import { DishNutrition } from "./DishNutrition";
import { hasNutritionTable } from "./dish-nutrition-format";

type DishTab = "details" | "nutrition";

interface DishInfoDrawerProps {
  dish: DishResponse;
  specialIcon?: React.ReactNode;
  highlightColor?: string;
  /** The same state as `highlightColor`, in the deeper tone words need. */
  highlightTextColor?: string;
  formattedPrice: string;
  matchedRecommendTags: DishTag[];
  matchedNeutralTags: DishTag[];
  matchedAllergens: DishAllergen[];
  matchedMayContainAllergens: DishAllergen[];
  matchedPreferences: DishPreference[];
  matchedIngredients: DishIngredient[];
  open: boolean;
  onClose?: () => void;
  onOpenAllergenIngredients?: () => void;
  showCalories: boolean;
  showMayContains: boolean;
  isIngredientFilteringEnabled?: boolean;
  containerRef?: React.RefObject<HTMLDivElement>;
  linkedItems?: GetAllResolvedEdiblesResponse;
  optionalIngredientIds?: Set<string>;
  showRelatedIngredientsFromProducts?: boolean;
}

/**
 * DishInfoDrawer displays detailed information about a dish, including allergens, ingredients,
 * preferences, tags, calories, and more. It highlights matched allergens and ingredients,
 * and allows users to see which items are optional (can be omitted). The component re-renders
 * when the dish, matched items, open state, or related props change.
 */
export const DishInfoDrawer = ({
  dish,
  formattedPrice,
  open,
  onClose,
  onOpenAllergenIngredients,
  specialIcon,
  highlightColor,
  highlightTextColor,
  matchedPreferences,
  matchedAllergens,
  matchedMayContainAllergens,
  matchedIngredients,
  matchedRecommendTags,
  matchedNeutralTags,
  showCalories,
  showMayContains,
  containerRef,
  optionalIngredientIds,
  showRelatedIngredientsFromProducts,
}: DishInfoDrawerProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { oscar } = theme.palette;
  const isMobile = useMediaQuery((t: Theme) => t.breakpoints.down("sm"));

  const loadDishDetail = useDishDetailLoader();
  const [nutritionEU, setNutritionEU] = useState<NutritionEU | null>(null);
  const [loadingNutrition, setLoadingNutrition] = useState(false);
  const [tab, setTab] = useState<DishTab>("details");

  useLayoutEffect(() => {
    if (!open) return;
    setTab("details");
    if (!loadDishDetail) return;
    let current = true;
    setNutritionEU(null);
    setLoadingNutrition(true);
    loadDishDetail(dish.id)
      .then((detail) => {
        if (current) {
          setNutritionEU(
            detail.nutritionEU && hasNutritionTable(detail.nutritionEU)
              ? detail.nutritionEU
              : null
          );
        }
      })
      .finally(() => {
        if (current) setLoadingNutrition(false);
      });
    return () => {
      current = false;
    };
  }, [open, dish.id, loadDishDetail]);

  const hasIngredient = !!matchedIngredients.length;

  const hasAllergens =
    !!matchedAllergens.length || !!matchedMayContainAllergens.length;
  const hasConfirmed =
    matchedAllergens.length > 0 || matchedIngredients.length > 0;
  const onlyMayContain = showMayContains && !hasConfirmed;
  const showsAllergens = showMayContains || hasAllergens || hasIngredient;
  const hasDetails =
    isSpiceLevel(dish.spiceLevel) ||
    !!matchedNeutralTags.length ||
    !!matchedPreferences.length ||
    !!matchedRecommendTags.length ||
    !!dish.description ||
    showsAllergens;

  const hasSubIngredientsForMatchedAllergens = useMemo(() => {
    if (!dish?.ingredients || !matchedAllergens.length) return false;
    // Check if any ingredient's allergen IDs overlap with matchedAllergens
    return dish.ingredients.some((ingredient) =>
      ingredient.igfdTaxAllergenIds?.some((id) =>
        matchedAllergens.some((ma) => ma.igfdTaxAllergenId === id)
      )
    );
  }, [dish.ingredients, matchedAllergens]);

  function isIngredientOptional(ingredient: DishIngredient): boolean {
    return optionalIngredientIds?.has(ingredient.igfdTaxIngredientId) ?? false;
  }

  // Memoized array of confirmed items (allergens and ingredients)
  const confirmedItems = useMemo(() => {
    const allergenItems =
      matchedAllergens.map((allergen) => ({
        id: allergen.igfdTaxAllergenId,
        name: allergen.allergenName,
        isOptional: !!allergen.isOptional,
        type: "allergen" as const,
      })) || [];
    const ingredientItems =
      matchedIngredients.map((ingredient) => ({
        id: ingredient.igfdTaxIngredientId,
        name: ingredient.ingredientName,
        isOptional: isIngredientOptional(ingredient),
        type: "ingredient" as const,
      })) || [];
    // Combine and sort: optional first, then alphabetically by name
    return [...allergenItems, ...ingredientItems].sort((a, b) => {
      if (a.isOptional !== b.isOptional) return a.isOptional ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [
    matchedAllergens,
    matchedIngredients,
    optionalIngredientIds,
    dish.allergens,
  ]);

  // Render helper for confirmed chip
  function renderConfirmedChip(item: {
    id: string;
    name: string;
    isOptional: boolean;
    type: string;
  }) {
    return (
      <AllergenChipOutlined
        key={item.id}
        allergenName={
          item.isOptional
            ? t("dish.itemCanBeRemoved", { name: item.name })
            : item.name
        }
        canOmit={item.isOptional}
      />
    );
  }

  return (
    <DynamicDrawer
      ref={containerRef}
      drawerType={isMobile ? "large" : "medium"}
      open={open}
      onClose={onClose}
      hasFooterBlur
      Footer={
        // A button rather than a bare svg: the icon alone had no name for a
        // screen reader, took no keyboard focus, and was under the 44px a
        // finger needs.
        <IconButton aria-label={t("common.close")} onClick={onClose}>
          <CloseIcon color={oscar.light.icon.black} height={32} width={32} />
        </IconButton>
      }
    >
      <Stack alignItems="center" justifyItems="flex-start" gap={0.5}>
        {dish.imageUrl ? (
          <Box marginBottom={2}>
            <ImagePreview
              imageUrl={dish.imageUrl}
              height="200px"
              width="350px"
              sx={{
                borderRadius: 2,
              }}
            />
          </Box>
        ) : null}
        <Stack alignItems="center" justifyItems="flex-start" gap={0.5}>
          <Stack flexDirection="row" alignItems="center" gap={1}>
            {specialIcon}
            <KnifeAndForkIcon
              color={highlightColor || oscar.light.icon.dark}
              height={32}
              width={32}
            />
          </Stack>
          <Typography
            color={highlightTextColor ?? highlightColor}
            variant="headingRLight"
            textAlign="center"
          >
            {dish.name}
          </Typography>
        </Stack>
        {formattedPrice ? (
          <Typography
            color={oscar.light.content.content2}
            variant="labelLMedium"
          >
            {formattedPrice}
          </Typography>
        ) : null}
        {!!showCalories && dish.caloriesInKcal && (
          <Typography
            color={oscar.light.content.content2}
            variant="labelLMedium"
          >
            {t("common.calories", { count: dish.caloriesInKcal })}
          </Typography>
        )}
      </Stack>
      {loadingNutrition ? (
        <CircularProgressCenter fullScreen={false} />
      ) : nutritionEU ? (
        <Box marginTop={1.5} marginBottom={2.5}>
          {hasDetails ? (
            <SegmentedControl
              ariaLabel={t("dish.tabs.label")}
              options={[
                { value: "details", label: t("dish.tabs.details") },
                { value: "nutrition", label: t("dish.tabs.nutrition") },
              ]}
              value={tab}
              onChange={setTab}
            />
          ) : (
            <Typography
              color={oscar.light.content.content1}
              variant="labelRMedium"
            >
              {t("dish.tabs.nutrition")}
            </Typography>
          )}
        </Box>
      ) : null}
      {loadingNutrition ? null : nutritionEU &&
        (tab === "nutrition" || !hasDetails) ? (
        <DishNutrition nutrition={nutritionEU} />
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          alignItems="center"
          width="100%"
        >
          <Box>
            <SpiceLevelIcon level={dish.spiceLevel} />
            {!!matchedNeutralTags.length ? (
              <Box
                alignItems="center"
                display="flex"
                flexWrap="wrap"
                justifyContent="center"
                gap={1}
                marginTop={1}
              >
                {matchedNeutralTags.map((tag) => (
                  <TagChip key={tag.id} tag={tag} />
                ))}
              </Box>
            ) : null}
          </Box>

          {matchedPreferences.length || matchedRecommendTags.length ? (
            <ChipRow>
              {matchedPreferences.map((preference) => (
                <PreferenceChipOutlined
                  key={preference.igfdTaxPreferenceId}
                  preferenceName={preference.preferenceName}
                />
              ))}
              {matchedRecommendTags.map((tag) => (
                <TagChip key={tag.id} tag={tag} />
              ))}
            </ChipRow>
          ) : null}

          {dish.description ? (
            <Typography
              color={theme.palette.oscar.light.content.content2}
              variant="bodyRLight"
              textAlign="center"
            >
              {dish.description}
            </Typography>
          ) : null}

          {showsAllergens ? (
            <>
              <Box
                alignItems="center"
                display="flex"
                flexDirection="column"
                gap={2}
                width="100%"
              >
                {matchedAllergens.length || matchedIngredients.length ? (
                  <>
                    <Typography
                      color={oscar.light.content.content1}
                      variant="labelRMedium"
                    >{`${confirmedItems.length} Confirmed ${
                      confirmedItems.length === 1 ? "allergen" : "allergens"
                    }`}</Typography>
                    <ChipRow justifyContent="center" flexWrap="wrap">
                      {confirmedItems.map(renderConfirmedChip)}
                    </ChipRow>
                  </>
                ) : null}
                {hasSubIngredientsForMatchedAllergens ||
                showRelatedIngredientsFromProducts ? (
                  <Typography
                    component="button"
                    type="button"
                    color={oscar.light.content.link}
                    variant="buttonS"
                    sx={{
                      cursor: "pointer",
                      background: "none",
                      border: "none",
                      padding: 0,
                    }}
                    onClick={onOpenAllergenIngredients}
                    aria-label={t("dish.showRelatedIngredients")}
                  >
                    {t("dish.relatedIngredients")}
                  </Typography>
                ) : null}
              </Box>
              {showMayContains && (
                <DishInfoMayContain
                  matchedMayContainAllergens={matchedMayContainAllergens}
                />
              )}
            </>
          ) : null}
        </Box>
      )}
    </DynamicDrawer>
  );
};

const ChipRow = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  justifyContent: "center",
  flexWrap: "wrap",
  gap: theme.spacing(1),
}));
